if (!customElements.get('break-in-wizard')) {
  customElements.define(
    'break-in-wizard',
    class BreakInWizard extends HTMLElement {
      constructor() {
        super();
        this.currentStep = 0;
        this.step1SubView = 'usage';
        this.step4SubView = 'thumb-pinky';
        this.totalSteps = 0;
        this.isModalMode = false;
        this.triggerButton = null;
      }

      connectedCallback() {
        // Guard: initModal() moves this element to document.body which
        // re-triggers connectedCallback — skip re-init to avoid duplicate listeners.
        if (this._initialized) return;
        this._initialized = true;

        this.panels = this.querySelectorAll('.wizard-panel');
        this.indicators = this.querySelectorAll('.wizard-step-indicator');
        this.connectors = this.querySelectorAll('.wizard-step-connector');
        this.srAnnouncements = this.querySelector('.wizard-sr-announcements');
        this.productFormId = this.dataset.productFormId;
        this.totalSteps = this.panels.length;

        this.bindEvents();
        this.initAgeControl();
        this.bindInfoTooltips();
        this.bindSummaryAccordions();
        this.updateUI();
        this.updateAddToCartState();
        this.applyPositionVariant();

        if (this.hasAttribute('data-modal-mode')) {
          this.initModal();
        }
      }

      initModal() {
        this.isModalMode = true;
        const sectionId = this.dataset.sectionId;

        // Find the trigger button (before we move to body)
        this.triggerButton = document.getElementById(`WizardTrigger-${sectionId}`);

        // Move wizard to document.body to avoid z-index stacking issues
        document.body.appendChild(this);

        // Bind trigger button
        this.triggerButton?.addEventListener('click', () => this.openModal());

        // Bind close button
        const closeBtn = this.querySelector('.wizard-modal__close');
        closeBtn?.addEventListener('click', () => this.closeModal());

        // Bind overlay backdrop click
        const overlay = this.querySelector('.wizard-modal__overlay');
        overlay?.addEventListener('click', () => this.closeModal());

        // Bind ESC key
        this._handleEsc = (e) => {
          if (e.key === 'Escape' && this.dataset.modalState === 'open') {
            this.closeModal();
          }
        };
        document.addEventListener('keydown', this._handleEsc);

        // Bind Add to Cart button inside wizard
        const addToCartBtn = this.querySelector('.wizard-add-to-cart');
        addToCartBtn?.addEventListener('click', () => {
          if (this.isAllComplete()) {
            this.closeModal();
            // Click the real product form submit button
            const productForm = document.querySelector(`product-form[data-section-id="${this.dataset.sectionId}"]`);
            const submitBtn = productForm?.querySelector('.product-form__submit');
            submitBtn?.click();
          }
        });
      }

      openModal() {
        this.dataset.modalState = 'open';
        document.body.classList.add('overflow-hidden');

        const dialog = this.querySelector('.wizard-modal__dialog');
        if (dialog && typeof trapFocus === 'function') {
          trapFocus(dialog);
        }

        // Focus first focusable element in current step
        const panel = this.panels[this.currentStep];
        if (panel) {
          const firstFocusable = panel.querySelector('input, button, textarea, select, [tabindex]:not([tabindex="-1"])');
          firstFocusable?.focus();
        }
      }

      closeModal() {
        this.dataset.modalState = 'closed';
        document.body.classList.remove('overflow-hidden');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.triggerButton);
        }

        this.triggerButton?.focus();
      }

      bindEvents() {
        // Use event delegation for nav buttons and summary edit buttons
        this.addEventListener('click', (e) => {
          if (e.target.closest('.wizard-nav-next') && !e.target.closest('.wizard-add-to-cart')) {
            this.handleNext();
          } else if (e.target.closest('.wizard-nav-back')) {
            if (this.currentStep === 0 && this.step1SubView === 'sport-type') {
              const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
              this.step1SubView = usageRadio && usageRadio.value === 'Recreation' ? 'usage' : 'position';
              this.updateStep1SubViews();
              this.updateContinueButtonState();
            } else if (this.currentStep === 0 && this.step1SubView === 'position') {
              this.step1SubView = 'usage';
              this.updateStep1SubViews();
              this.updateContinueButtonState();
            } else if (this.currentStep === 3 && this.step4SubView === 'fingers') {
              this.step4SubView = 'thumb-pinky';
              this.updateStep4SubViews();
              this.updateContinueButtonState();
            } else if (this.currentStep === 1) {
              // Going back to Step 0 — both paths end on the sport-type sub-view
              const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
              if (usageRadio) {
                this.goToStep(0, 'sport-type');
              } else {
                this.goToStep(0, 'usage');
              }
            } else if (this.currentStep === 4 && this.step4SubView !== 'thumb-pinky') {
              // Going back within Step 4 fielding — land on fingers sub-view
              this.goToStep(3, 'fingers');
            } else {
              this.goToStep(this.currentStep - 1);
            }
          } else if (e.target.closest('.wizard-summary-edit')) {
            const btn = e.target.closest('.wizard-summary-edit');
            const step = parseInt(btn.dataset.goToStep, 10);
            const subView = btn.dataset.goToSubview || null;
            if (!isNaN(step)) this.goToStep(step, subView);
          }
        });

        // Step indicator clicks
        this.indicators.forEach((indicator, index) => {
          indicator.addEventListener('click', () => {
            if (this.canNavigateToStep(index)) {
              this.goToStep(index);
            }
          });
        });

        // Radio button changes
        this.querySelectorAll('.wizard-options input[type="radio"]').forEach((radio) => {
          radio.addEventListener('change', () => {
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Text input changes
        this.querySelectorAll('.wizard-text-input').forEach((input) => {
          input.addEventListener('input', () => {
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Select changes
        this.querySelectorAll('.wizard-select').forEach((select) => {
          select.addEventListener('change', () => {
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Position checkboxes
        this.querySelectorAll('.wizard-position-checkbox').forEach((checkbox) => {
          checkbox.addEventListener('change', (e) => {
            if (this.dataset.gloveType === 'catcher' && e.target.checked) {
              const currentValue = e.target.value;
              this.querySelectorAll('.wizard-position-checkbox:not(:disabled)').forEach((other) => {
                if (other !== e.target && other.value !== currentValue) {
                  other.checked = false;
                }
              });
            }
            this.updatePositionHiddenInput();
            this.applyPositionVariant();
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Disabled position click — show popup pointing to correct service
        this.querySelectorAll('.wizard-position-spot--disabled').forEach((spot) => {
          spot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.showDisabledPositionAlert();
          });
        });

        // Sport type checkboxes
        this.querySelectorAll('.wizard-sport-type-checkbox').forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            this.updateSportTypeHiddenInput();
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Usage radio buttons
        this.querySelectorAll('[data-usage-select]').forEach((radio) => {
          radio.addEventListener('change', () => {
            this.updateSportTypeLabels();
            this.updateAddToCartState();
            this.updateContinueButtonState();
          });
        });

        // Terms checkbox
        const termsCheckbox = this.querySelector('.wizard-terms input[type="checkbox"]');
        termsCheckbox?.addEventListener('change', () => {
          this.updateAddToCartState();
          this.updateContinueButtonState();
        });

        // Listen for cart add success to reset wizard
        document.addEventListener('cart:item-added', () => this.reset());

        // Also listen to Shopify's cart update event
        if (typeof subscribe === 'function') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'product-form') {
              this.reset();
            }
          });
        }
      }

      initAgeControl() {
        const wrap = this.querySelector('[data-wizard-age-control]');
        if (!wrap) return;

        const number  = wrap.querySelector('[data-wizard-age-number]');
        const hidden  = this.querySelector('[data-wizard-age-hidden]');
        const errorEl = this.querySelector('#wizard-age-error');
        const stepBtns = wrap.querySelectorAll('[data-wizard-age-step]');
        if (!number || !hidden) return;

        this.ageDefault = 12;
        this.ageMin = 0;
        this.ageMax = 100;
        this._ageEls = { wrap, number, hidden, errorEl, stepBtns };

        const setFromValue = (raw, { source } = {}) => {
          const trimmed = String(raw ?? '').trim();
          if (trimmed === '') {
            hidden.value = '';
            this._setAgeInvalid(true, 'Please enter an age between 0 and 100.');
            this._updateStepperButtons(null);
            this.updateContinueButtonState();
            this.updateAddToCartState();
            return false;
          }
          if (!/^-?\d+$/.test(trimmed)) {
            this._setAgeInvalid(true);
            this.updateContinueButtonState();
            this.updateAddToCartState();
            return false;
          }
          let n = parseInt(trimmed, 10);
          if (source === 'blur' || source === 'step') {
            n = Math.max(this.ageMin, Math.min(this.ageMax, n));
          } else if (n < this.ageMin || n > this.ageMax) {
            this._setAgeInvalid(true);
            this.updateContinueButtonState();
            this.updateAddToCartState();
            return false;
          }
          const s = String(n);
          if (source !== 'number' && number.value !== s) number.value = s;
          hidden.value = s;
          this._updateStepperButtons(n);
          this._setAgeInvalid(false);
          this.updateContinueButtonState();
          this.updateAddToCartState();
          return true;
        };

        number.addEventListener('input', () => setFromValue(number.value, { source: 'number' }));
        number.addEventListener('blur', () => {
          if (number.value.trim() === '') {
            number.value = String(this.ageDefault);
            setFromValue(number.value, { source: 'blur' });
            return;
          }
          setFromValue(number.value, { source: 'blur' });
          if (hidden.value) number.value = hidden.value;
        });
        number.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            number.blur();
            if (this.validateCurrentStep()) this.handleNext();
          }
        });

        stepBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = parseInt(btn.dataset.wizardAgeStep, 10) || 0;
            const current = parseInt(hidden.value, 10);
            const base = Number.isNaN(current) ? this.ageDefault : current;
            const next = Math.max(this.ageMin, Math.min(this.ageMax, base + delta));
            setFromValue(String(next), { source: 'step' });
            number.focus();
          });
        });

        setFromValue(number.value || String(this.ageDefault), { source: 'init' });
        this._setAgeValue = setFromValue;
      }

      _updateStepperButtons(n) {
        const btns = this._ageEls && this._ageEls.stepBtns;
        if (!btns) return;
        btns.forEach((btn) => {
          const delta = parseInt(btn.dataset.wizardAgeStep, 10) || 0;
          if (n === null || Number.isNaN(n)) {
            btn.removeAttribute('disabled');
            return;
          }
          if (delta < 0 && n <= this.ageMin) {
            btn.setAttribute('disabled', '');
          } else if (delta > 0 && n >= this.ageMax) {
            btn.setAttribute('disabled', '');
          } else {
            btn.removeAttribute('disabled');
          }
        });
      }

      _setAgeInvalid(invalid, msg) {
        const els = this._ageEls || {};
        const number = els.number;
        const errorEl = els.errorEl;
        if (!number) return;
        number.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        if (errorEl) {
          if (invalid) {
            errorEl.hidden = false;
            if (msg) errorEl.textContent = msg;
          } else {
            errorEl.hidden = true;
          }
        }
      }

      _isAgeValid() {
        const hidden = this._ageEls && this._ageEls.hidden;
        if (!hidden) return false;
        const v = hidden.value.trim();
        if (v === '' || !/^\d+$/.test(v)) return false;
        const n = parseInt(v, 10);
        return n >= this.ageMin && n <= this.ageMax;
      }

      handleNext() {
        // Step 0 sub-views: usage → position → sport-type → step 2
        if (this.currentStep === 0) {
          if (this.step1SubView === 'usage') {
            const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
            if (!usageRadio) return;
            if (usageRadio.value === 'Recreation') {
              // Recreation skips position selection but still asks sport type
              this.querySelectorAll('.wizard-position-checkbox').forEach((cb) => { cb.checked = false; });
              this.updatePositionHiddenInput();
              this.step1SubView = 'sport-type';
            } else {
              this.step1SubView = 'position';
            }
            this.updateStep1SubViews();
            this.updateContinueButtonState();
            return;
          }

          if (this.step1SubView === 'position') {
            if (!this.validateCurrentStep()) return;
            this.step1SubView = 'sport-type';
            this.updateStep1SubViews();
            this.updateContinueButtonState();
            return;
          }

          if (this.step1SubView === 'sport-type') {
            if (!this.validateCurrentStep()) return;
            this.goToStep(1);
            return;
          }
        }

        // Step 3 (index 3) sub-views for fielding: thumb-pinky → fingers → step 5
        if (this.currentStep === 3 && this.step4SubView === 'thumb-pinky') {
          if (!this.validateCurrentStep()) return;
          this.step4SubView = 'fingers';
          this.updateStep4SubViews();
          this.updateContinueButtonState();
          return;
        }

        if (!this.validateCurrentStep()) {
          return;
        }

        if (this.currentStep < this.totalSteps - 1) {
          this.goToStep(this.currentStep + 1);
        }
      }

      goToStep(stepIndex, subView = null) {
        if (stepIndex < 0 || stepIndex >= this.totalSteps) return;

        this.closeAllTooltips();
        this.currentStep = stepIndex;

        // Determine step 0 sub-view
        if (stepIndex === 0) {
          if (subView) {
            this.step1SubView = subView;
          } else {
            const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
            if (usageRadio && usageRadio.value === 'Sports / Competition') {
              this.step1SubView = 'position';
            } else if (usageRadio && usageRadio.value === 'Recreation') {
              this.step1SubView = 'sport-type';
            } else {
              this.step1SubView = 'usage';
            }
          }
        }

        // Determine step 3 sub-view (fielding only)
        if (stepIndex === 3) {
          if (subView) {
            this.step4SubView = subView;
          } else {
            this.step4SubView = 'thumb-pinky';
          }
        }

        this.updateUI();
        this.announceStep();

        // Focus the first focusable element in the active sub-view or panel
        const panel = this.panels[stepIndex];
        let focusContainer = panel;
        if (stepIndex === 0) {
          const subViewSelector = {
            'usage': '.wizard-step1-usage',
            'position': '.wizard-step1-position',
            'sport-type': '.wizard-step1-sport-type',
          }[this.step1SubView];
          const subViewEl = subViewSelector ? panel.querySelector(subViewSelector) : null;
          if (subViewEl) focusContainer = subViewEl;
        }
        if (stepIndex === 3) {
          const subViewSelector = {
            'thumb-pinky': '.wizard-step4-thumb-pinky',
            'fingers': '.wizard-step4-fingers',
          }[this.step4SubView];
          const subViewEl = subViewSelector ? panel.querySelector(subViewSelector) : null;
          if (subViewEl) focusContainer = subViewEl;
        }
        // Prefer the age number input when this panel has the stepper.
        const ageNumber = focusContainer.querySelector('[data-wizard-age-number]');
        if (ageNumber) {
          ageNumber.focus();
        } else {
          const firstFocusable = focusContainer.querySelector('input, button, textarea, select, [tabindex]:not([tabindex="-1"])');
          firstFocusable?.focus();
        }
      }

      canNavigateToStep(stepIndex) {
        // Can always go back
        if (stepIndex < this.currentStep) return true;

        // Can only go forward one step at a time, and only if current step is valid
        if (stepIndex === this.currentStep + 1) {
          return this.validateCurrentStep();
        }

        // Can go to completed steps
        if (stepIndex <= this.getHighestCompletedStep()) {
          return true;
        }

        return false;
      }

      getHighestCompletedStep() {
        let highest = -1;
        for (let i = 0; i < this.totalSteps; i++) {
          if (this.isStepComplete(i)) {
            highest = i;
          } else {
            break;
          }
        }
        return highest;
      }

      isStepComplete(stepIndex) {
        const panel = this.panels[stepIndex];
        if (!panel) return false;

        // Special handling for step 0 (usage + position + sport type)
        if (stepIndex === 0) {
          const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
          if (!usageRadio) return false;
          // Sports/Competition requires at least one position; Recreation skips positions
          if (usageRadio.value !== 'Recreation') {
            const posCheckboxes = panel.querySelectorAll('.wizard-position-checkbox:not(:disabled)');
            if (posCheckboxes.length > 0) {
              if (!Array.from(posCheckboxes).some((cb) => cb.checked)) return false;
            }
          }
          // Both paths require at least one sport type
          const sportCheckboxes = panel.querySelectorAll('.wizard-sport-type-checkbox');
          if (sportCheckboxes.length > 0) {
            if (!Array.from(sportCheckboxes).some((cb) => cb.checked)) return false;
          }
          return true;
        }

        // Player Age (Step 2) — slider/number; require numeric 0–100.
        if (panel.querySelector('[data-wizard-age-control]')) {
          if (!this._isAgeValid()) return false;
        }

        // Check radio buttons grouped by name
        const radios = panel.querySelectorAll('input[type="radio"]');
        if (radios.length > 0) {
          const radioGroups = new Map();
          radios.forEach((r) => {
            if (!radioGroups.has(r.name)) radioGroups.set(r.name, []);
            radioGroups.get(r.name).push(r);
          });
          for (const [, groupRadios] of radioGroups) {
            if (!groupRadios.some((r) => r.checked)) return false;
          }
        }

        // Check position checkboxes
        const positionCheckboxes = panel.querySelectorAll('.wizard-position-checkbox:not(:disabled)');
        if (positionCheckboxes.length > 0) {
          const anyChecked = Array.from(positionCheckboxes).some((cb) => cb.checked);
          if (!anyChecked) return false;
        }

        // Check required text inputs and selects
        const requiredInputs = panel.querySelectorAll('[data-wizard-required]');
        for (const input of requiredInputs) {
          const isEmpty = input.tagName === 'SELECT' ? !input.value : input.value.trim() === '';
          if (isEmpty) return false;
        }

        // Check terms checkbox (only on last step)
        const termsCheckbox = panel.querySelector('.wizard-terms input[type="checkbox"]');
        if (termsCheckbox && !termsCheckbox.checked) {
          return false;
        }

        return true;
      }

      validateCurrentStep() {
        const panel = this.panels[this.currentStep];
        if (!panel) return false;

        // Special handling for step 0 sub-views
        if (this.currentStep === 0) {
          if (this.step1SubView === 'usage') {
            return !!this.querySelector('input[name="properties[Glove Usage]"]:checked');
          } else if (this.step1SubView === 'position') {
            const positionCheckboxes = panel.querySelectorAll('.wizard-position-checkbox:not(:disabled)');
            if (positionCheckboxes.length > 0) {
              return Array.from(positionCheckboxes).some((cb) => cb.checked);
            }
            return true;
          } else if (this.step1SubView === 'sport-type') {
            const sportTypeCheckboxes = panel.querySelectorAll('.wizard-sport-type-checkbox');
            if (sportTypeCheckboxes.length > 0) {
              return Array.from(sportTypeCheckboxes).some((cb) => cb.checked);
            }
            return true;
          }
        }

        // Special handling for step 3 sub-views (fielding: thumb-pinky / fingers)
        if (this.currentStep === 3 && this.querySelector('.wizard-step4-thumb-pinky')) {
          const activeSubView = this.step4SubView === 'thumb-pinky'
            ? panel.querySelector('.wizard-step4-thumb-pinky')
            : panel.querySelector('.wizard-step4-fingers');
          if (activeSubView) {
            const subRadios = activeSubView.querySelectorAll('input[type="radio"]');
            if (subRadios.length > 0) {
              const radioGroups = new Map();
              subRadios.forEach((r) => {
                if (!radioGroups.has(r.name)) radioGroups.set(r.name, []);
                radioGroups.get(r.name).push(r);
              });
              for (const [, groupRadios] of radioGroups) {
                if (!groupRadios.some((r) => r.checked)) return false;
              }
            }
            return true;
          }
        }

        // Player Age (Step 2) — slider/number; require numeric 0–100.
        if (panel.querySelector('[data-wizard-age-control]')) {
          if (!this._isAgeValid()) return false;
        }

        // Check radio buttons grouped by name
        const radios = panel.querySelectorAll('input[type="radio"]');
        if (radios.length > 0) {
          const radioGroups = new Map();
          radios.forEach((r) => {
            if (!radioGroups.has(r.name)) radioGroups.set(r.name, []);
            radioGroups.get(r.name).push(r);
          });
          for (const [, groupRadios] of radioGroups) {
            if (!groupRadios.some((r) => r.checked)) return false;
          }
        }

        // Check position checkboxes
        const positionCheckboxes = panel.querySelectorAll('.wizard-position-checkbox:not(:disabled)');
        if (positionCheckboxes.length > 0) {
          if (!Array.from(positionCheckboxes).some((cb) => cb.checked)) return false;
        }

        // Check required text inputs and selects
        const requiredInputs = panel.querySelectorAll('[data-wizard-required]');
        for (const input of requiredInputs) {
          const isEmpty = input.tagName === 'SELECT' ? !input.value : input.value.trim() === '';
          if (isEmpty) return false;
        }

        // Check terms checkbox
        const termsCheckbox = panel.querySelector('.wizard-terms input[type="checkbox"]');
        if (termsCheckbox && !termsCheckbox.checked) return false;

        return true;
      }

      updateUI() {
        // Update panels visibility
        this.panels.forEach((panel, index) => {
          const isActive = index === this.currentStep;
          panel.setAttribute('aria-hidden', !isActive);
          panel.setAttribute('tabindex', isActive ? '0' : '-1');
          panel.style.display = isActive ? 'block' : 'none';
        });

        // Toggle step 0 sub-views
        this.updateStep1SubViews();

        // Toggle step 3 sub-views (fielding)
        this.updateStep4SubViews();

        // Update step indicators
        this.indicators.forEach((indicator, index) => {
          const isActive = index === this.currentStep;
          const isComplete = index < this.currentStep || (index === this.currentStep && this.isStepComplete(index));

          indicator.setAttribute('aria-selected', isActive);
          indicator.classList.toggle('completed', isComplete && index < this.currentStep);
          indicator.disabled = !this.canNavigateToStep(index);
        });

        // Update connectors
        this.connectors.forEach((connector, index) => {
          connector.classList.toggle('completed', index < this.currentStep);
        });

        // Update summary on last step
        if (this.currentStep === this.totalSteps - 1) {
          this.updateSummary();
        }

        // Update navigation buttons in current panel
        const currentPanel = this.panels[this.currentStep];
        if (currentPanel) {
          const backBtn = currentPanel.querySelector('.wizard-nav-back');
          const nextBtn = currentPanel.querySelector('.wizard-nav-next:not(.wizard-add-to-cart)');
          const addToCartBtn = currentPanel.querySelector('.wizard-add-to-cart');

          if (backBtn) {
            backBtn.hidden = this.currentStep === 0;
          }

          const isLastStep = this.currentStep === this.totalSteps - 1;
          if (nextBtn) {
            nextBtn.textContent = isLastStep ? 'Complete Setup' : 'Continue';
            nextBtn.hidden = isLastStep;
          }

          // Show Add to Cart on last step, disabled until all complete
          if (addToCartBtn) {
            addToCartBtn.hidden = !isLastStep;
            addToCartBtn.disabled = !this.isAllComplete();
          }
        }

        this.updateContinueButtonState();
      }

      updateStep1SubViews() {
        const usageView = this.querySelector('.wizard-step1-usage');
        const positionView = this.querySelector('.wizard-step1-position');
        const sportTypeView = this.querySelector('.wizard-step1-sport-type');
        if (usageView) usageView.style.display = this.step1SubView === 'usage' ? '' : 'none';
        if (positionView) positionView.style.display = this.step1SubView === 'position' ? '' : 'none';
        if (sportTypeView) sportTypeView.style.display = this.step1SubView === 'sport-type' ? '' : 'none';
        this.updateSportTypeLabels();
      }

      updateSportTypeLabels() {
        const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
        const isRecreation = usageRadio && usageRadio.value === 'Recreation';
        const heading = this.querySelector('[data-sport-type-heading]');
        if (heading) {
          heading.textContent = isRecreation ? heading.dataset.titleRecreation : heading.dataset.titleCompetition;
        }
        const label = this.querySelector('[data-sport-type-label]');
        if (label) {
          label.textContent = isRecreation ? label.dataset.labelRecreation : label.dataset.labelCompetition;
        }
        // Rename the cart line item property so cart/checkout/orders match the wizard wording
        const propertyName = isRecreation ? 'properties[Catch Type]' : 'properties[Sport Type]';
        const hiddenInput = this.querySelector('#wizard-sport-type-hidden');
        if (hiddenInput) {
          hiddenInput.name = propertyName;
        }
        const summaryRow = this.querySelector('[data-summary-for="properties[Sport Type]"], [data-summary-for="properties[Catch Type]"]');
        if (summaryRow) {
          summaryRow.dataset.summaryFor = propertyName;
        }
      }

      updateStep4SubViews() {
        const thumbPinkyView = this.querySelector('.wizard-step4-thumb-pinky');
        const fingersView = this.querySelector('.wizard-step4-fingers');
        if (thumbPinkyView) thumbPinkyView.style.display = this.step4SubView === 'thumb-pinky' ? '' : 'none';
        if (fingersView) fingersView.style.display = this.step4SubView === 'fingers' ? '' : 'none';
      }

      updateContinueButtonState() {
        const currentPanel = this.panels[this.currentStep];
        if (!currentPanel) return;

        // Scope to active sub-view so we find the correct Continue button
        let container = currentPanel;
        if (this.currentStep === 0) {
          const sel = { 'usage': '.wizard-step1-usage', 'position': '.wizard-step1-position', 'sport-type': '.wizard-step1-sport-type' }[this.step1SubView];
          if (sel) container = currentPanel.querySelector(sel) || currentPanel;
        } else if (this.currentStep === 3) {
          const sel = { 'thumb-pinky': '.wizard-step4-thumb-pinky', 'fingers': '.wizard-step4-fingers' }[this.step4SubView];
          if (sel) container = currentPanel.querySelector(sel) || currentPanel;
        }

        const nextBtn = container.querySelector('.wizard-nav-next:not(.wizard-add-to-cart)');
        if (!nextBtn || nextBtn.hidden) return;

        nextBtn.disabled = !this.validateCurrentStep();
      }

      isAllComplete() {
        for (let i = 0; i < this.totalSteps; i++) {
          if (!this.isStepComplete(i)) return false;
        }
        return true;
      }

      updateAddToCartState() {
        const isComplete = this.isAllComplete();

        // Find the product form and toggle the submit button
        const productForm = document.querySelector(`product-form[data-section-id="${this.dataset.sectionId}"]`);
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          if (!isComplete) {
            productForm.toggleSubmitButton(true, 'Complete Setup Above');
          } else {
            productForm.toggleSubmitButton(false);
          }
        }

        // Also set a data attribute for CSS-based targeting
        this.dataset.wizardComplete = isComplete;

        // Update trigger button state
        this.updateTriggerButton(isComplete);

        // Update wizard Add to Cart button disabled state
        const wizardAddToCart = this.querySelector('.wizard-add-to-cart');
        if (wizardAddToCart) {
          wizardAddToCart.disabled = !isComplete;
        }

        // Dispatch event for any listeners
        this.dispatchEvent(new CustomEvent('wizard:state-change', {
          bubbles: true,
          detail: { complete: isComplete }
        }));
      }

      updateTriggerButton(isComplete) {
        if (!this.triggerButton) return;
        const textEl = this.triggerButton.querySelector('.wizard-trigger-btn__text');
        if (isComplete) {
          this.triggerButton.classList.add('wizard-trigger-btn--complete');
          if (textEl) textEl.textContent = 'Edit Customization';
        } else {
          this.triggerButton.classList.remove('wizard-trigger-btn--complete');
          const gloveType = this.dataset.gloveType;
          const gloveLabel = gloveType === 'catcher' ? '1st/Catchers Mitt' : 'Field Glove';
          if (textEl) textEl.textContent = `Customize Your ${gloveLabel} Break-In`;
        }
      }

      updateSummary() {
        this.updateSportTypeLabels();
        const summaryRows = this.querySelectorAll('.wizard-summary-row');
        const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
        summaryRows.forEach((row) => {
          const propertyName = row.dataset.summaryFor;
          const valueEl = row.querySelector('[data-summary-value]');
          if (!propertyName || !valueEl) return;

          let value = '';

          // Hide Position row when usage is Recreation (Sport Type shows for both paths)
          if (propertyName === 'properties[Position]' && usageRadio && usageRadio.value === 'Recreation') {
            row.style.display = 'none';
            return;
          } else if (propertyName === 'properties[Position]') {
            row.style.display = '';
          }

          // Try checked radio
          if (!value) {
            const checkedRadio = this.querySelector(`input[name="${propertyName}"]:checked`);
            if (checkedRadio) value = checkedRadio.value;
          }

          // Try text input
          if (!value) {
            const textInput = this.querySelector(`input[name="${propertyName}"].wizard-text-input`);
            if (textInput && textInput.value.trim()) value = textInput.value.trim();
          }

          // Try select
          if (!value) {
            const select = this.querySelector(`select[name="${propertyName}"]`);
            if (select && select.value) value = select.value;
          }

          // Try hidden input (for Position, Sport Type)
          if (!value) {
            const hiddenInput = this.querySelector(`input[name="${propertyName}"][type="hidden"]`);
            if (hiddenInput && hiddenInput.value) value = hiddenInput.value;
          }

          // Try textarea (for Additional Notes)
          if (!value) {
            const textarea = this.querySelector(`textarea[name="${propertyName}"]`);
            if (textarea && textarea.value.trim()) value = textarea.value.trim();
          }

          // Hide row if empty, show if filled
          if (value) {
            valueEl.textContent = value;
            row.style.display = '';
          } else {
            row.style.display = 'none';
          }
        });
      }

      updatePositionHiddenInput() {
        const hiddenInput = this.querySelector('#wizard-position-hidden');
        if (!hiddenInput) return;
        const usageRadio = this.querySelector('input[name="properties[Glove Usage]"]:checked');
        if (usageRadio && usageRadio.value === 'Recreation') {
          hiddenInput.value = '';
          return;
        }
        const checkboxes = this.querySelectorAll('.wizard-position-checkbox:not(:disabled):checked');
        const values = Array.from(checkboxes).map((cb) => cb.value);
        hiddenInput.value = values.join(', ');
      }

      applyPositionVariant() {
        if (this.dataset.gloveType !== 'catcher') return;
        const firstBaseChecked = !!this.querySelector('.wizard-position-checkbox[value="First Base"]:checked');
        const variant = firstBaseChecked ? 'firstbase' : 'catcher';
        const panels = this.querySelectorAll('#wizard-panel-3, #wizard-panel-4');
        panels.forEach((panel) => {
          panel.querySelectorAll('img[data-src-catcher]').forEach((img) => {
            const next = img.dataset[variant === 'firstbase' ? 'srcFirstbase' : 'srcCatcher'];
            if (next) {
              img.src = next;
            }
          });
          panel.querySelectorAll('[data-desc-catcher]').forEach((node) => {
            const next = node.dataset[variant === 'firstbase' ? 'descFirstbase' : 'descCatcher'];
            if (next) {
              node.textContent = next;
            }
          });
          panel.querySelectorAll('[data-position-only="catcher"]').forEach((wrapper) => {
            const hide = variant === 'firstbase';
            wrapper.hidden = hide;
            if (hide) {
              const input = wrapper.querySelector('input[type="radio"], input[type="checkbox"]');
              if (input && input.checked) {
                input.checked = false;
              }
            }
          });
        });
      }

      updateSportTypeHiddenInput() {
        const hiddenInput = this.querySelector('#wizard-sport-type-hidden');
        if (!hiddenInput) return;
        const checkboxes = this.querySelectorAll('.wizard-sport-type-checkbox:checked');
        const values = Array.from(checkboxes).map((cb) => cb.value);
        hiddenInput.value = values.join(', ');
      }

      announce(message) {
        if (this.srAnnouncements) {
          this.srAnnouncements.textContent = message;
          // Clear after a moment so it can be announced again if needed
          setTimeout(() => {
            this.srAnnouncements.textContent = '';
          }, 1000);
        }
      }

      announceStep() {
        const stepNum = this.currentStep + 1;
        const panel = this.panels[this.currentStep];
        let question = '';
        if (this.currentStep === 0) {
          const subViewSelector = {
            'usage': '.wizard-step1-usage',
            'position': '.wizard-step1-position',
            'sport-type': '.wizard-step1-sport-type',
          }[this.step1SubView];
          const subViewEl = subViewSelector ? panel?.querySelector(subViewSelector) : null;
          question = subViewEl?.querySelector('.wizard-question')?.textContent || '';
        } else if (this.currentStep === 3 && this.querySelector('.wizard-step4-thumb-pinky')) {
          const subViewSelector = {
            'thumb-pinky': '.wizard-step4-thumb-pinky',
            'fingers': '.wizard-step4-fingers',
          }[this.step4SubView];
          const subViewEl = subViewSelector ? panel?.querySelector(subViewSelector) : null;
          question = subViewEl?.querySelector('.wizard-question')?.textContent || '';
        } else {
          question = panel?.querySelector('.wizard-question')?.textContent || '';
        }
        this.announce(`Step ${stepNum} of ${this.totalSteps}. ${question}`);
      }

      bindSummaryAccordions() {
        this.querySelectorAll('.wizard-summary-group details').forEach((details) => {
          const summary = details.querySelector('summary');
          const content = details.querySelector('.accordion__content');
          if (!summary || !content) return;
          summary.addEventListener('click', (e) => {
            if (!details.open) return;
            e.preventDefault();
            details.setAttribute('data-closing', '');
            content.addEventListener('transitionend', () => {
              details.removeAttribute('data-closing');
              details.removeAttribute('open');
            }, { once: true });
          });
        });
      }

      bindInfoTooltips() {
        const toggles = this.querySelectorAll('.wizard-option-info__toggle');
        if (!toggles.length) return;

        // Click toggle (works on both mobile and desktop)
        toggles.forEach((toggle) => {
          const tooltip = toggle.nextElementSibling;
          if (!tooltip || !tooltip.classList.contains('wizard-option-info__tooltip')) return;

          toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleTooltip(toggle, tooltip);
          });

          // Desktop hover on the info button itself
          toggle.addEventListener('mouseenter', () => {
            if (window.matchMedia('(hover: hover)').matches) {
              this.openTooltip(toggle, tooltip);
            }
          });
          toggle.addEventListener('mouseleave', (e) => {
            if (window.matchMedia('(hover: hover)').matches) {
              // Keep open if moving into the tooltip
              if (e.relatedTarget && tooltip.contains(e.relatedTarget)) return;
              this.closeTooltip(toggle, tooltip);
            }
          });
          tooltip.addEventListener('mouseleave', (e) => {
            if (window.matchMedia('(hover: hover)').matches) {
              // Keep open if moving back to the toggle
              if (e.relatedTarget && e.relatedTarget === toggle) return;
              this.closeTooltip(toggle, tooltip);
            }
          });
        });

        // Escape key closes all tooltips
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.closeAllTooltips();
          }
        });

        // Click outside closes all tooltips
        document.addEventListener('click', (e) => {
          if (!e.target.closest('.wizard-option-info__toggle') && !e.target.closest('.wizard-option-info__tooltip')) {
            this.closeAllTooltips();
          }
        });
      }

      toggleTooltip(toggle, tooltip) {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        this.closeAllTooltips();
        if (!isOpen) {
          this.openTooltip(toggle, tooltip);
        }
      }

      openTooltip(toggle, tooltip) {
        toggle.setAttribute('aria-expanded', 'true');
        tooltip.hidden = false;
      }

      closeTooltip(toggle, tooltip) {
        toggle.setAttribute('aria-expanded', 'false');
        tooltip.hidden = true;
      }

      closeAllTooltips() {
        this.querySelectorAll('.wizard-option-info__toggle[aria-expanded="true"]').forEach((toggle) => {
          const tooltip = toggle.nextElementSibling;
          if (tooltip) {
            this.closeTooltip(toggle, tooltip);
          }
        });
      }

      showDisabledPositionAlert() {
        // Remove any existing alert
        const existing = this.querySelector('.wizard-disabled-alert');
        if (existing) existing.remove();

        const gloveType = this.dataset.gloveType;
        const message = gloveType === 'fielding'
          ? 'This position requires a catcher\'s/1st base mitt break-in service.'
          : 'This position requires a fielding glove break-in service.';

        const alert = document.createElement('div');
        alert.className = 'wizard-disabled-alert';
        alert.textContent = message;

        // Append inside the field container (position: relative) so it overlays without layout shift
        const fieldContainer = this.querySelector('.wizard-position-field-container');
        if (fieldContainer) {
          fieldContainer.appendChild(alert);
        }

        // Fade out then remove
        setTimeout(() => {
          alert.classList.add('is-fading');
          alert.addEventListener('transitionend', () => alert.remove(), { once: true });
        }, 2500);
      }

      reset() {
        this.closeAllTooltips();
        this.currentStep = 0;
        this.step1SubView = 'usage';
        this.step4SubView = 'thumb-pinky';

        // Close modal if open
        if (this.isModalMode && this.dataset.modalState === 'open') {
          this.closeModal();
        }

        // Reset all radio buttons
        this.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = false;
        });

        // Reset position checkboxes and hidden input
        this.querySelectorAll('.wizard-position-checkbox').forEach((cb) => {
          cb.checked = false;
        });
        const positionHidden = this.querySelector('#wizard-position-hidden');
        if (positionHidden) {
          positionHidden.value = '';
        }

        // Reset sport type checkboxes and hidden input
        this.querySelectorAll('.wizard-sport-type-checkbox').forEach((cb) => {
          cb.checked = false;
        });
        const sportTypeHidden = this.querySelector('#wizard-sport-type-hidden');
        if (sportTypeHidden) {
          sportTypeHidden.value = '';
        }

        // Reset text inputs
        this.querySelectorAll('.wizard-text-input').forEach((input) => {
          input.value = '';
        });

        // Reset age stepper/number/hidden to default
        if (typeof this._setAgeValue === 'function') {
          const def = String(this.ageDefault);
          const els = this._ageEls || {};
          if (els.number) els.number.value = def;
          if (els.hidden) els.hidden.value = def;
          this._setAgeValue(def, { source: 'init' });
        }

        // Reset selects
        this.querySelectorAll('.wizard-select').forEach((select) => {
          select.selectedIndex = 0;
        });

        // Reset textarea
        const textarea = this.querySelector('.wizard-textarea');
        if (textarea) {
          textarea.value = '';
        }

        // Reset terms checkbox
        const termsCheckbox = this.querySelector('.wizard-terms input[type="checkbox"]');
        if (termsCheckbox) {
          termsCheckbox.checked = false;
        }

        // Reset trigger button text
        this.updateTriggerButton(false);

        this.updateUI();
        this.updateAddToCartState();
      }
    }
  );
}
